/**
 * setupPatterns — composite pattern detector.
 *
 * Runs all 7 Wave C pattern detectors at the entry bar and returns the
 * set of patterns that fired. Stored on `trade.barAnalysis.setupPatterns`
 * via maeMfe so downstream consumers (tradeFlags chips, WR-by-pattern
 * analyzer, future probability engine) read from one place.
 *
 * Each detector is independent — failures in one don't poison the rest.
 * A trade can fire MULTIPLE patterns simultaneously (e.g. Wolf Stack +
 * Holy Grail on a high-conviction pullback entry).
 *
 * Pure function. No side effects.
 */

import { detectWolfStack } from './patterns/wolfStack.js';
import { detectSignalBarQuality } from './patterns/signalBarQuality.js';
import { detectHolyGrail } from './patterns/holyGrail.js';
import { detectThreeBarPlay } from './patterns/threeBarPlay.js';
import { detectTurtleSoup } from './patterns/turtleSoup.js';
import { detectUnoReverse } from './patterns/unoReverse.js';
import { detectH2L2 } from './patterns/h2l2.js';
import { detectMaCross921 } from './patterns/maCross921.js';

// Catalog: type → { detector, label, severity-on-trade-card, timeframe,
//                    watchFor + optional watchForLong / watchForShort }.
// Used by the chip renderer + WR analyzer + Live Coach Feed so the pattern
// list stays in one place.
//
// Fields:
//   label         Human-readable name shown on chips and live cards.
//   chipSeverity  Severity tone for trade-card chip rendering.
//                   'good' = positive signal; 'info' = situation-dependent.
//   timeframe     The bar timeframe this detector evaluates against. All
//                 detectors currently run on 5m bars (the live poll TF).
//                 Surfaced on the Live Coach Feed so traders know which
//                 chart they should be looking at when the pattern fires.
//                 Future enhancement: per-TF detection (15m holy grail etc.).
//   watchFor      Actionable post-fire guidance — one short sentence. Tells
//                 the trader the natural next thing to look for as
//                 confirmation that the setup is working. Not predictive
//                 (the probabilities row handles that); this is execution
//                 hygiene — "now what?"
//                 Used as the FALLBACK when watchForLong / watchForShort
//                 aren't provided.
//   watchForLong  Direction-specific override for long fires (v7.5.11).
//                 Some patterns (wolf-stack, holy-grail, 3-bar-play) have
//                 inherently directional follow-throughs — "above the EMAs"
//                 only makes sense for longs. When present, this string
//                 wins over `watchFor` for long-direction fires.
//   watchForShort Direction-specific override for short fires (v7.5.11).
//
// Resolution: PatternBlock in LiveCoachFeed picks watchForLong / watchForShort
// based on `fire.direction`, falling back to the generic `watchFor`.
export const PATTERN_CATALOG = Object.freeze({
  'wolf-stack': {
    label: 'Wolf Stack',
    chipSeverity: 'good',
    timeframe: '5m',
    // EMA stack is inherently directional — for shorts it stacks downward
    // (price below 9 < 21 < 50) so "above" is backwards.
    watchForLong:  'Continuation candle that holds above the stacked EMAs.',
    watchForShort: 'Continuation candle that holds below the stacked EMAs.',
  },
  'signal-bar-quality': {
    label: 'Signal Bar',
    chipSeverity: 'good',
    timeframe: '5m',
    // "in direction" already covers both — no override needed.
    watchFor: 'Confirmation bar in direction — wait for breakout close, not just wick.',
  },
  'holy-grail': {
    label: 'Holy Grail',
    chipSeverity: 'good',
    timeframe: '5m',
    // Pullback to 20 EMA in an uptrend → reclaim. In a downtrend → rejection.
    // ADX expanding is direction-agnostic so that part stays.
    watchForLong:  'Reclaim of the 20 EMA with rising momentum (ADX expanding).',
    watchForShort: 'Rejection of the 20 EMA with rising momentum (ADX expanding).',
  },
  '3-bar-play': {
    label: '3-Bar Play',
    chipSeverity: 'good',
    timeframe: '5m',
    // Clearer to split rather than the previous "high (long) or low (short)".
    watchForLong:  'Break of the 3-bar high on volume.',
    watchForShort: 'Break of the 3-bar low on volume.',
  },
  'turtle-soup': {
    label: 'Turtle Soup',
    chipSeverity: 'info',  // can be good OR bad — caller checks alignsWithTrade
    timeframe: '5m',
    // Direction-agnostic — "reverse the false breakout" works either way.
    watchFor: 'Clean reversal of the false breakout — failed breakout closes back inside the range.',
  },
  'uno-reverse': {
    label: 'Uno Reverse',
    chipSeverity: 'good',
    timeframe: '5m',
    // Direction-agnostic — "original trend direction" is self-orienting.
    watchFor: 'Resumption candle in the original trend direction.',
  },
  'h2': {
    label: 'H2',
    chipSeverity: 'good',
    timeframe: '5m',
    // H2 is always long-side by definition (second-leg long after pullback).
    watchFor: 'Break above the prior pullback high — second leg up.',
  },
  'l2': {
    label: 'L2',
    chipSeverity: 'good',
    timeframe: '5m',
    // L2 is always short-side by definition.
    watchFor: 'Break below the prior pullback low — second leg down.',
  },
});

/**
 * Run all pattern detectors and return the array of matches.
 *
 * @param {Object} params
 * @param {Array}  params.bars        5m bars covering the entry.
 * @param {number} params.entryTs     Unix seconds.
 * @param {number} params.entryPrice  Trade entry price.
 * @param {string} params.direction   'long' | 'short' | aliases.
 * @param {number} [params.adxAtEntry]  Pre-computed ADX (from trendContextScore)
 *                                     to avoid recomputing for Holy Grail.
 * @returns {Array<{ type, score, maxScore, ...detectorSpecific }>}
 *          Empty array when no patterns fire OR inputs insufficient.
 */
export const detectSetupPatterns = ({ bars, entryTs, entryPrice, direction, adxAtEntry } = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) return [];
  if (!Number.isFinite(entryTs)) return [];

  const params = { bars, entryTs, entryPrice, direction };
  const out = [];

  const safeRun = (name, fn) => {
    try {
      const r = fn();
      if (r) out.push(r);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[setupPatterns] ${name} detector failed:`, e?.message || e);
    }
  };

  safeRun('wolfStack',         () => detectWolfStack(params));
  safeRun('signalBarQuality',  () => detectSignalBarQuality(params));
  safeRun('holyGrail',         () => detectHolyGrail({ ...params, adxAtEntry }));
  safeRun('threeBarPlay',      () => detectThreeBarPlay(params));
  safeRun('turtleSoup',        () => detectTurtleSoup(params));
  safeRun('unoReverse',        () => detectUnoReverse(params));
  safeRun('h2l2',              () => detectH2L2(params));
  safeRun('maCross921',        () => detectMaCross921(params));

  return out;
};
